/**
 * Date helpers used across the PHM frontend.
 * Keep in sync with backend app/utils/dates.py where applicable.
 */

/** Returns ISO yyyy-mm-dd for today. */
export function todayISO() {
    return new Date().toISOString().slice(0, 10);
}

/**
 * The one display format for dates across the whole app.
 * Also the placeholder shown in every date input.
 */
export const DATE_FORMAT = 'YYYY/MM/DD';

/** Returns today as "2026/06/22". */
export function formatToday() {
    return formatDateDisplay(todayISO());
}

/** Convert "yyyy-mm-dd" (or a full ISO timestamp) -> "yyyy/mm/dd" for display. */
export function formatDateDisplay(dateString) {
    if (!dateString) return '';
    return String(dateString).slice(0, 10).replace(/-/g, '/');
}

/**
 * Convert an ISO timestamp -> "yyyy/mm/dd hh:mm" (or ":ss" when withSeconds).
 * Returns `fallback` for empty input.
 */
export function formatDateTimeDisplay(iso, { withSeconds = false, fallback = '—' } = {}) {
    if (!iso) return fallback;
    const s = String(iso);
    const time = s.slice(11, withSeconds ? 19 : 16);
    return time ? `${formatDateDisplay(s)} ${time}` : formatDateDisplay(s);
}

/** Convert "yyyy/mm/dd" typed by a user -> ISO "yyyy-mm-dd", or null if invalid. */
export function parseDateDisplay(text) {
    const m = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(String(text || '').trim());
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    // Date.UTC + UTC getters — keeps the date stable regardless of local TZ.
    const dt = new Date(Date.UTC(y, mo - 1, d));
    if (
        dt.getUTCFullYear() !== y ||
        dt.getUTCMonth() + 1 !== mo ||
        dt.getUTCDate() !== d
    ) {
        return null; // rejects e.g. 2026/02/31
    }
    return `${m[1]}-${m[2]}-${m[3]}`;
}

/** Convert a Date to "yyyy-mm-dd" (for <input type="date"> values). */
export function toDateInputValue(d) {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Months (whole) between dob and "today" or given reference. */
export function monthsBetween(dob, ref = new Date()) {
    if (!dob) return 0;
    const start = new Date(dob);
    const end = ref instanceof Date ? ref : new Date(ref);
    let months = (end.getFullYear() - start.getFullYear()) * 12;
    months -= start.getMonth();
    months += end.getMonth();
    if (end.getDate() < start.getDate()) months -= 1;
    return Math.max(0, months);
}

/** Decimal age in years (used for growth CDSS tab selection). */
export function decimalAgeYears(dob, ref = new Date()) {
    if (!dob) return 0;
    const start = new Date(dob).getTime();
    const end = (ref instanceof Date ? ref : new Date(ref)).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return 0;
    return (end - start) / (1000 * 60 * 60 * 24 * 365.25);
}

/**
 * Human-readable age label (matches backend age_at_record taxonomy).
 * Examples: "Newborn", "3 Days", "6 Weeks", "8 Months", "2 Years, 3 Months".
 */
export function calculateAge(dob, ref = new Date()) {
    return ageLabel(dob, ref);
}

export function ageLabel(dob, ref = new Date()) {
    if (!dob) return '';
    const start = new Date(dob);
    const end = ref instanceof Date ? ref : new Date(ref);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';

    let months = (end.getFullYear() - start.getFullYear()) * 12;
    months -= start.getMonth();
    months += end.getMonth();
    if (end.getDate() < start.getDate()) months -= 1;
    if (months < 0) return '';

    if (months < 3) {
        const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        if (days <= 0) return 'Newborn';
        if (days < 7) return `${days} Day${days > 1 ? 's' : ''}`;
        const weeks = Math.floor(days / 7);
        return `${weeks} Week${weeks > 1 ? 's' : ''}`;
    }
    if (months < 12) return `${months} Month${months !== 1 ? 's' : ''}`;
    const years = Math.floor(months / 12);
    const remaining = months % 12;
    if (remaining === 0) return `${years} Year${years > 1 ? 's' : ''}`;
    return `${years} Year${years > 1 ? 's' : ''}, ${remaining} Month${remaining !== 1 ? 's' : ''}`;
}